import * as React from 'react';
import { isEmpty } from 'ramda';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '../../components';
import { getPaymentPlans, PaymentPlan } from '../../context/storeContext';
import useFetchAsync from '../../hooks/useFetchAsync';
import { paths } from '../../routes';
import getAppURL from '../../utils/getAppURL';
import getCookie from '../../utils/getCookie';
import getSubdomain from '../../utils/getSubdomain';
import isOnTestServer from '../../utils/isOnTestServer';

function Home() {
  const { data, status, error, run } = useFetchAsync();

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subdomain = getSubdomain(window.location.origin);

  const store =
    searchParams.get('store') ||
    JSON.parse(getCookie('_pos_store') || '{}')?.name ||
    '';

  const { paymentPlans: plans } = (data || {}) as {
    paymentPlans: PaymentPlan[];
  };

  if (subdomain) {
    // For now any subdomain should be a logged in shop
    const host = window.location.origin.replace(`${subdomain}.`, 'www.');
    window.location.replace(`${host}${location.pathname}?store=${subdomain}`);
  }

  const dashboardPath = React.useMemo(() => {
    const url = new URL(getAppURL());
    const { protocol, host } = url;

    if (!(store && !isEmpty(store))) {
      return '';
    }

    if (isOnTestServer()) {
      return `${protocol}//${host}/${store}/dashboard`;
    }
    return `${protocol}//${store}.${host.replace('www.', '')}/dashboard`;
  }, [store]);

  React.useEffect(() => {
    document.body.style.backgroundColor = '#ffde00';
  }, []);

  React.useEffect(() => {
    const fetchingPaymentPlans = getPaymentPlans();
    run(fetchingPaymentPlans);
  }, [run]);

  return (
    <div className="bg-pos-yellow p-6 tablet:p-10 laptop:p-6 h-screen flex flex-col justify-between">
      <div className="w-full laptop:container mx-auto">
        <header className="w-full">
          <div className="w-full flex items-center justify-between">
            <a
              className="flex items-center text-pos-black font-semibold text-2xl shrink-0"
              href={paths.LANDING_HOME_URL_PATH}
            >
              Swiftinflow
            </a>

            <div className="flex flex-row w-full justify-end content-center space-x-3 items-center">
              <a
                className="hidden tablet:inline-flex items-center justify-center text-pos-hyperlink no-underline 
                text-center h-[40px] w-fit text-base font-normal hover:underline"
                href="tel:+233549961446"
              >
                +233 54 996 1446
              </a>

              <a
                onClick={() =>
                  navigate(paths.LANDING_PRICING_URL_PATH, {
                    state: { plans },
                  })
                }
                className="hidden tablet:inline-flex items-center justify-center text-pos-black no-underline text-center 
                h-[35px] w-fit text-base font-medium hover:bg-pos-black hover:text-pos-yellow px-3 rounded-md"
                href={paths.LANDING_PRICING_URL_PATH}
              >
                Pricing
              </a>

              {store && dashboardPath ? (
                <a
                  className="inline-flex items-center justify-center text-pos-black border-2 border-pos-black rounded-md no-underline
                  hover:bg-pos-black hover:text-pos-yellow text-center h-[40px] w-fit px-3 text-base font-medium"
                  href={dashboardPath}
                >
                  Dashboard
                </a>
              ) : (
                <>
                  <a
                    className="inline-flex items-center justify-center text-pos-black no-underline text-center 
                    h-[35px] w-fit text-base font-medium hover:bg-pos-black hover:text-pos-yellow px-3 rounded-md"
                    href={paths.LOGIN_URL_PATH}
                  >
                    Login
                  </a>

                  <a
                    className="hidden tablet:inline-flex items-center justify-center text-pos-black border-2 border-pos-black rounded-md no-underline
                  hover:bg-pos-black hover:text-pos-yellow text-center h-[40px] w-fit px-3 text-base font-medium"
                    href={paths.REGISTER_URL_PATH}
                  >
                    Start free trial
                  </a>
                </>
              )}
            </div>
          </div>
        </header>

        <div className="w-full laptop:w-[60%] mx-auto pt-16 tablet:pt-24">
          <div className="flex flex-col w-full justify-center items-center">
            <h1 className="text-3xl tablet:text-6xl text-pos-black font-bold !leading-normal tablet:!leading-[1.2] text-center">
              All In One control center for your store
            </h1>

            <p className="mt-5 tablet:mt-8 text-[#8f7319] leading-normal text-lg tablet:text-lg text-center">
              With features easily tailored for your needs
            </p>

            <div className="flex flex-row space-x-5 items-center justify-center mt-9">
              {store && dashboardPath ? (
                <a
                  className="bg-pos-black text-white font-bold py-2 px-6 rounded focus:ring transform 
                  transition hover:scale-105 duration-300 ease-in-out"
                  href={dashboardPath}
                >
                  Go to store
                </a>
              ) : (
                <>
                  <button
                    onClick={() =>
                      navigate(paths.LANDING_PRICING_URL_PATH, {
                        state: { plans },
                      })
                    }
                    className="bg-pos-black text-white font-bold py-2 px-4 rounded focus:ring transform 
                    transition hover:scale-105 duration-300 ease-in-out whitespace-pre tablet:whitespace-normal"
                    type="button"
                  >
                    Pay now & save 50%
                  </button>
                  <button
                    onClick={() => navigate(paths.REGISTER_URL_PATH)}
                    className="bg-pos-black text-white font-bold py-2 px-4 rounded focus:ring transform 
                    transition hover:scale-105 duration-300 ease-in-out whitespace-pre tablet:whitespace-normal"
                    type="button"
                  >
                    Try for free
                  </button>
                </>
              )}
            </div>

            <div className="grid grid-cols-3 gap-8 w-full mt-14 tablet:mt-20 text-center text-base items-baseline">
              <div className="flex flex-col justify-center items-center font-bold ">
                <div className="group w-10 h-10 tablet:w-12 tablet:h-12 rounded-md bg-pos-black flex flex-row items-center justify-center mb-2">
                  <Icon
                    id="iconPOSNav"
                    className="h-auto w-4 tablet:w-6 object-cover fill-pos-yellow transform transition group-hover:scale-125 duration-500 ease-in-out"
                  />
                </div>
                <span className="hidden tablet:inline">Point Of Sale</span>
                <span className="tablet:hidden">POS</span>
              </div>

              <div className="flex flex-col justify-center items-center font-bold">
                <div className="group w-10 h-10 tablet:w-12 tablet:h-12 rounded-md bg-pos-black flex flex-row items-center justify-center mb-2">
                  <Icon
                    id="iconProductNav"
                    className="h-auto w-4 tablet:w-6 object-cover fill-pos-yellow transform transition group-hover:scale-125 duration-500 ease-in-out"
                  />
                </div>
                Inventory
              </div>

              <div className="flex flex-col justify-center items-center font-bold">
                <div className="group w-10 h-10 tablet:w-12 tablet:h-12 rounded-md bg-pos-black flex flex-row items-center justify-center mb-2">
                  <Icon
                    id="iconReportsNav"
                    className="h-auto w-4 tablet:w-6 object-cover fill-pos-yellow transform transition group-hover:scale-125 duration-500 ease-in-out"
                  />
                </div>
                Reporting
              </div>

              <div className="flex flex-col justify-center items-center font-bold">
                <div className="group w-10 h-10 tablet:w-12 tablet:h-12 rounded-md bg-pos-black flex flex-row items-center justify-center mb-2">
                  <Icon
                    id="iconActionsNav"
                    className="h-auto w-4 tablet:w-6 object-cover fill-pos-yellow transform transition group-hover:scale-125 duration-500 ease-in-out"
                  />
                </div>
                Customization
              </div>

              <div className="flex flex-col justify-center items-center font-bold">
                <div className="group w-10 h-10 tablet:w-12 tablet:h-12 rounded-md bg-pos-black flex flex-row items-center justify-center mb-2">
                  <Icon
                    id="iconStore"
                    className="h-auto w-4 tablet:w-6 object-cover fill-pos-yellow transform transition group-hover:scale-125 duration-500 ease-in-out"
                  />
                </div>
                Multi-store
              </div>

              <div className="flex flex-col justify-center items-center font-bold">
                <div className="group w-10 h-10 tablet:w-12 tablet:h-12 rounded-md bg-pos-black flex flex-row items-center justify-center mb-2">
                  <Icon
                    id="iconCallSupport"
                    className="h-auto w-4 tablet:w-6 object-cover fill-pos-yellow transform transition group-hover:scale-125 duration-500 ease-in-out"
                  />
                </div>
                24/7 Local Support
              </div>
            </div>

            <p className="tablet:hidden leading-normal text-base text-center mt-8">
              To talk to us, call{' '}
              <a
                className="inline-flex text-pos-blue no-underline hover:underline"
                href="tel:+233549961446"
              >
                +233 54 996 1446
              </a>
            </p>
          </div>
        </div>
      </div>

      <footer className="w-full laptop:container mt-6 mx-auto text-sm text-center tablet:text-left fade-in">
        <a className="text-[#8f7319] no-underline hover:no-underline" href="#">
          &copy; {new Date().getFullYear()} Swiftinflow POS. All rights reserved
        </a>
      </footer>
    </div>
  );
}

export default Home;
